import React, { useState, useEffect, useRef } from 'react';
import styles from './about.module.css';
import dash1 from '../../../assets/dashboard screenshots/dash1.png';
import dash2 from '../../../assets/dashboard screenshots/dash2.png';
import dash3 from '../../../assets/dashboard screenshots/dash3.png';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPlatformStats } from '../../../api/platformApi';

const About = () => {
  const { t } = useTranslation();
  const sectionRefs = useRef([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [stats, setStats] = useState({ hotels: 0, properties: 0, providers: 0 });

  // Dashboard screenshots data
  const dashboardScreenshots = [
    {
      src: dash1,
      alt: "Spotfli Dashboard Overview"
    },
    {
      src: dash2, 
      alt: "Booking Management Dashboard"
    },
    {
      src: dash3,
      alt: "Analytics & Reports Dashboard"
    },
    {
      src: dash1,
      alt: "Property Management Dashboard"
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % dashboardScreenshots.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [dashboardScreenshots.length]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % dashboardScreenshots.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + dashboardScreenshots.length) % dashboardScreenshots.length);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchStats = async () => {
      const res = await getPlatformStats();
      if (isMounted && res?.success && res?.data) {
        setStats({
          hotels: Number(res.data.hotels) || 0,
          properties: Number(res.data.properties) || 0,
          providers: Number(res.data.providers) || 0,
        });
      }
    };
    fetchStats();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) {
      sectionRefs.current.push(el);
    }
  };

  return (
    <div className={styles.aboutContainer}>
      {/* Hero Section */}
      <section className={styles.aboutHero}>
        <div className={styles.heroContent}>
<div className={styles.heroText} ref={addToRefs}>
             <h1 className={styles.heroTitle}>
               {t('about.hero_title')}
             </h1>
             <p className={styles.heroSubtitle}>
               {t('about.hero_subtitle')}
             </p>
           </div>
          <div className={styles.heroVisual} ref={addToRefs}>
            <div className={styles.floatingCards}>
              <div className={`${styles.card} ${styles.card1}`}>
                <div className={styles.cardIcon}>🏠</div>
                <span>Properties</span>
              </div>
              <div className={`${styles.card} ${styles.card2}`}>
                <div className={styles.cardIcon}>🔧</div>
                <span>Services</span>
              </div>
              <div className={`${styles.card} ${styles.card3}`}>
                <div className={styles.cardIcon}>🏨</div>
                <span>Hotels</span>
              </div>
            </div>
          </div>
        </div>
        <br /><br /><br /><br /><br />
        <div className={styles.waveDivider}>
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" className={styles.shapeFill}></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" className={styles.shapeFill}></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" className={styles.shapeFill}></path>
          </svg>
        </div>
      </section>

{/* Mission Section */}
       <section className={styles.missionSection}>
         <div className={styles.container}>
           <div className={styles.missionContent}>
             <div className={styles.missionText} ref={addToRefs}>
               <h2>{t('about.mission_title')}</h2>
               <p>
                 {t('about.mission_text')}
               </p>
             </div>
             <div className={styles.missionStats}>
               <div className={styles.stat} ref={addToRefs}>
                 <div className={styles.statNumber}>{stats.properties > 0 ? `${stats.properties}+` : '0'}</div>
                 <div className={styles.statLabel}>{t('about.mission_stat_properties')}</div>
               </div>
               <div className={styles.stat} ref={addToRefs}>
                 <div className={styles.statNumber}>{stats.hotels > 0 ? `${stats.hotels}+` : '0'}</div>
                 <div className={styles.statLabel}>{t('about.mission_stat_hotels')}</div>
               </div>
               <div className={styles.stat} ref={addToRefs}>
                 <div className={styles.statNumber}>{stats.providers > 0 ? `${stats.providers}+` : '0'}</div>
                 <div className={styles.statLabel}>{t('about.mission_stat_providers')}</div>
               </div>
             </div>
           </div>
         </div>
       </section>

{/* Features Section */}
       <section className={styles.featuresSection}>
         <div className={styles.container}>
           <h2 className={styles.sectionTitle} ref={addToRefs}>
             {t('about.features_title')}
           </h2>
           <div className={styles.featuresGrid}>
             <div className={styles.featureCard} ref={addToRefs}>
               <div className={styles.featureIcon}>🔒</div>
               <h3>{t('about.feature_secure_payments.title')}</h3>
               <p>
                 {t('about.feature_secure_payments.description')}
               </p>
             </div>
             <div className={styles.featureCard} ref={addToRefs}>
               <div className={styles.featureIcon}>⚡</div>
               <h3>{t('about.feature_direct_comm.title')}</h3>
               <p>
                 {t('about.feature_direct_comm.description')}
               </p>
             </div>
             <div className={styles.featureCard} ref={addToRefs}>
               <div className={styles.featureIcon}>📱</div>
               <h3>{t('about.feature_mobile.title')}</h3>
               <p>
                 {t('about.feature_mobile.description')}
               </p>
             </div>
             <div className={styles.featureCard} ref={addToRefs}>
               <div className={styles.featureIcon}>🎯</div>
               <h3>{t('about.feature_local.title')}</h3>
               <p>
                 {t('about.feature_local.description')}
               </p>
             </div>
           </div>
         </div>
       </section>

{/* Hotel Benefits Section */}
       <section className={styles.hotelBenefits}>
         <div className={styles.container}>
           <div className={styles.benefitsContent}>
             <div className={styles.benefitsText} ref={addToRefs}>
               <h2>{t('about.benefits_title')}</h2>
               <div className={styles.benefitItem}>
                 <div className={styles.benefitIcon}>💰</div>
                 <div>
                   <h4>{t('about.benefit_revenue.title')}</h4>
                   <p>
                     {t('about.benefit_revenue.description')}
                   </p>
                 </div>
               </div>
               <div className={styles.benefitItem}>
                 <div className={styles.benefitIcon}>📊</div>
                 <div>
                   <h4>{t('about.benefit_management.title')}</h4>
                   <p>
                     {t('about.benefit_management.description')}
                   </p>
                 </div>
               </div>
               <div className={styles.benefitItem}>
                 <div className={styles.benefitIcon}>👥</div>
                 <div>
                   <h4>{t('about.benefit_reach.title')}</h4>
                   <p>
                     {t('about.benefit_reach.description')}
                   </p>
                 </div>
               </div>
             </div>
             <div className={styles.benefitsVisual} ref={addToRefs}>
              <div className={styles.dashboardCarousel}>
                <div className={styles.carouselContainer}>
                  {dashboardScreenshots.map((screenshot, index) => (
                    <div 
                      key={index}
                      className={`${styles.carouselItem} ${index === activeSlide ? styles.active : ''}`}
                    >
                      <img 
                        src={screenshot.src} 
                        alt={screenshot.alt}
                        className={styles.dashboardImage}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Navigation Arrows */}
                <div className={styles.carouselArrows}>
                  <button className={styles.carouselArrow} onClick={prevSlide}>
                    ‹
                  </button>
                  <button className={styles.carouselArrow} onClick={nextSlide}>
                    ›
                  </button>
                </div>
                
                {/* Navigation Dots */}
                <div className={styles.carouselNav}>
                  {dashboardScreenshots.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.carouselDot} ${index === activeSlide ? styles.active : ''}`}
                      onClick={() => setActiveSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Payment Security Section */}
       <section className={styles.paymentSecurity}>
         <div className={styles.container}>
           <div className={styles.securityContent}>
             <div className={styles.securityVisual} ref={addToRefs}>
               <div className={styles.paymentFlow}>
                 <div className={styles.flowStep}>
                   <div className={styles.stepNumber}>1</div>
                   <div className={styles.stepText}>{t('about.payment_step1')}</div>
                 </div>
                 <div className={styles.flowArrow}>→</div>
                 <div className={styles.flowStep}>
                   <div className={styles.stepNumber}>2</div>
                   <div className={styles.stepText}>{t('about.payment_step2')}</div>
                 </div>
                 <div className={styles.flowArrow}>→</div>
                 <div className={styles.flowStep}>
                   <div className={styles.stepNumber}>3</div>
                   <div className={styles.stepText}>{t('about.payment_step3')}</div>
                 </div>
                 <div className={styles.flowArrow}>→</div>
                 <div className={styles.flowStep}>
                   <div className={styles.stepNumber}>4</div>
                   <div className={styles.stepText}>{t('about.payment_step4')}</div>
                 </div>
               </div>
             </div>
             <div className={styles.securityText} ref={addToRefs}>
               <h2>{t('about.payment_title')}</h2>
               <p>
                 {t('about.payment_text')}
               </p>
               <ul className={styles.securityFeatures}>
                 <li>🔐 {t('about.payment_feature1')}</li>
                 <li>⏱️ {t('about.payment_feature2')}</li>
                 <li>📞 {t('about.payment_feature3')}</li>
                 <li>🏆 {t('about.payment_feature4')}</li>
               </ul>
             </div>
           </div>
         </div>
       </section>
       
       {/* Team Section */}
       <section className={styles.teamSection}>
         <div className={styles.container}>
           <h2 className={styles.sectionTitle} ref={addToRefs}>
             {t('about.team_title')}
           </h2>
           <p className={styles.teamDescription} ref={addToRefs}>
             {t('about.team_description')}
           </p>
           <div className={styles.teamValues}>
             <div className={styles.valueCard} ref={addToRefs}>
               <h3>{t('about.value_innovation')}</h3>
               <p>{t('about.value_innovation_desc')}</p>
             </div>
             <div className={styles.valueCard} ref={addToRefs}>
               <h3>{t('about.value_trust')}</h3>
               <p>{t('about.value_trust_desc')}</p>
             </div>
             <div className={styles.valueCard} ref={addToRefs}>
               <h3>{t('about.value_community')}</h3>
               <p>{t('about.value_community_desc')}</p>
             </div>
           </div>
         </div>
       </section>

{/* CTA Section */}
       <section className={styles.ctaSection}>
         <div className={styles.container}>
           <div className={styles.ctaContent} ref={addToRefs}>
             <h2>{t('about.cta_title')}</h2>
             <p>
               {t('about.cta_description')}
             </p>
             <div className={styles.ctaButtons}>
               <Link to='/'>
                 <button className={`${styles.ctaBtn} ${styles.primary}`}>{t('about.cta_get_started')}</button>
               </Link>
               <button className={`${styles.ctaBtn} ${styles.secondary}`}>{t('about.cta_learn_more')}</button>
             </div>
           </div>
         </div>
       </section>
    </div>
  );
};

export default About;